
import { of, Observable, Subject } from 'rxjs';

import { withLatestFrom, filter, debounceTime, retry, share, tap, catchError, map, switchMap } from 'rxjs/operators';
import { TrackingActions } from './../tracking/tracking.action';
import { UsersAction } from './users.actions';
import { UserManagerService } from './../../services/users/user-manager.service';
import { Injectable } from '@angular/core';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { RoutesTable } from '../../resources/routes-table.model';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { AuthenticationAttemptResult } from '../../enum/login/authentication-result.enum';
import { BackendService } from '../../services/backend/backend.service';
import { EnvironmentAction } from '../environment/environment.action';
import { UserProfileActions } from '../userprofile/userprofile.actions';
import { InitializerAction } from '../initializer/initiliazer.action';
import { SettingsService } from '../../services/settings/settings.service';
import { Router } from '@angular/router';
import { DigitalSubscriptionAction } from '../digital-subscription/digital-subscription.action';
import { CblcType } from '../../enum/clbc-type.enum';

const loginAttempDeniedMessage = 'Dados incorretos. Digite novamente.';

@Injectable()
export class UsersEffects {
    /**
     * Authenticate user.
     * @method authenticate
     */
    @Effect()
    public authenticate$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.login),
            switchMap((actionPayload: any) => {
                return this.userManagerService.authenticate(
                    actionPayload.payload.data.username,
                    actionPayload.payload.data.password,
                    actionPayload.payload.data.captchaSiteKey,
                    actionPayload.payload.data.captchaToken,
                    actionPayload.payload.data.cblcAccountCode,
                    actionPayload.payload.token,
                )
                    .pipe(
                        map((result: any) => {
                            if (result.access_token) {
                                this.userManagerService.saveUserPayload(result.access_token);

                                if (result.realtime_token) {
                                    this.userManagerService.saveRealtimePayload(result.realtime_token);
                                    this.store.dispatch({ type: UsersAction.realtimeTokenReceivedSuccess, payload: result.realtime_token });
                                }

                                const dataJWT = UserManagerService.extractUserData(result.access_token);
                                if ((dataJWT as any).isFirstActivatedLogin === true || (dataJWT as any).isFirstActivatedLogin === 'True') {
                                    this.store.dispatch({ type: TrackingActions.sendFirstLoginActivated, payload: { cblc: (dataJWT as any).cblc } });
                                }
                                this.store.dispatch({ type: UsersAction.isUserLogin, payload: true });
                                return { type: UsersAction.loginSuccess, payload: dataJWT };
                            }

                            return { type: UsersAction.loginDenied, payload: loginAttempDeniedMessage };
                        }),
                        catchError((error) => {
                            const payload = error.error;
                            if (error.status === 400) {
                                // usuário bloqueado
                                if (AuthenticationAttemptResult.Blocked.equals(payload.error)) {
                                    return of({
                                        type: UsersAction.loginDenied,
                                        payload: { authenticationFailedCount: 5 },
                                    });
                                }

                                // captcha inválido
                                if (AuthenticationAttemptResult.InvalidCaptcha.equals(payload.error)) {
                                    return of({
                                        type: UsersAction.loginDenied,
                                        payload: { message: loginAttempDeniedMessage, authenticationFailedCount: 4 },
                                    });
                                }

                                // necessário selecionar uma conta
                                if (AuthenticationAttemptResult.CblcNotInformed.equals(payload.error)) {
                                    return of({
                                        type: UsersAction.accountSelectRequired,
                                        payload: JSON.parse(payload.error_description).$values,
                                    });
                                }

                                // Token necessário
                                if (AuthenticationAttemptResult.SecondFactorRequired.equals(payload.error)) {
                                    return of({
                                        type: UsersAction.tokenRequired,
                                        payload: JSON.parse(payload.error_description),
                                    });
                                }

                                // Usuário não encontrado
                                if (AuthenticationAttemptResult.UserNotFound.equals(payload.error)) {
                                    return of({
                                        type: UsersAction.loginDenied,
                                        payload: { message: loginAttempDeniedMessage, authenticationFailedCount: 0 },
                                    });
                                }

                                // Token Not Found
                                if (AuthenticationAttemptResult.TokenNotFound.equals(payload.error) ||
                                    AuthenticationAttemptResult.TokenExpired.equals(payload.error) ||
                                    AuthenticationAttemptResult.TokenInvalid.equals(payload.error)) {
                                    return of({
                                        type: UsersAction.tokenError,
                                        payload: { message: loginAttempDeniedMessage, authenticationFailedCount: 0 },
                                    });
                                }

                                // quantidade de erros
                                const attemptsCount = +payload.error_description.getOnlyNumbers();
                                return of({
                                    type: UsersAction.loginDenied,
                                    payload: { message: loginAttempDeniedMessage, authenticationFailedCount: attemptsCount },
                                });
                            }

                            const responseServer = error.headers && error.headers.get && error.headers.get('server');
                            const errorFromElb = responseServer && responseServer.includes && responseServer.includes('elb');

                            // não foi possível alcançar o servidor ou um erro inesperado ocorreu
                            return of({
                                type: UsersAction.loginDenied,
                                payload: { message: loginAttempDeniedMessage, authenticationFailedCount: 0, errorFromElb },
                            });
                        }));

            }),
            share());

    /**
     * Authenticate user attempt.
     * @method loginAttempt$
     */
    @Effect()
    public loginAttempt$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.login),
            map((action: any) => {
                return { type: UsersAction.loginAttempt, payload: { username: action.payload.data.username } };
            }));

    /**
     * Authenticated
     * @method loginSuccess
     */
    @Effect({ dispatch: false })
    public loginSuccess$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.loginSuccess),
            tap((action: any) => {
                if (this.settingsService.getConfigurationKeyValue('eec') !== true) {
                    // tslint:disable-next-line: no-console
                    console.warn('[App Lifecycle info] environment change disabled by environment setting');
                } else {
                    this.store.dispatch({ type: EnvironmentAction.getEnvironments });
                }
                this.store.dispatch({ type: UserProfileActions.getUserToroUser, payload: action.payload });
                this.store.dispatch({ type: DigitalSubscriptionAction.getPurchase });
                this.store.dispatch({ type: InitializerAction.connectToRealTime, payload: action.payload.token });

                if (!action.payload.cblc) {
                    this.store.dispatch({ type: UsersAction.requestUpdatedCblc })
                }
            }));

    /** User logout */
    @Effect()
    public logout$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.logout),
            tap((action) => {
                this.store.dispatch({ type: UsersAction.resetCurrentUser });
                this.store.dispatch({ type: EnvironmentAction.disableEnvironmentSwitching });
                this.store.dispatch({ type: InitializerAction.resetUserRelatedData });
                this.store.dispatch({ type: InitializerAction.getSessionID, payload: this.settingsService.getSettingsSnapshot() });
            }),
            switchMap((action: any) => {

                this.userManagerService.clearUserData();
                return this.userManagerService.logout()
                    .pipe(
                        map(() => {
                            this.userManagerService.destroyRememberToken();
                            window.location.href = '/';
                            return { type: UsersAction.logoutSuccess };
                        }),
                        catchError(() => of({ type: UsersAction.logoutError })));
            }),
            share());

    @Effect({ dispatch: false })
    public resetCurrentUser$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.resetCurrentUser),
            share());

    /** User password update */
    // tslint:disable-next-line: member-ordering
    @Effect()
    public changePassword$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.changePassword),
            withLatestFrom(this.store.select((state) => state.authentication)),
            switchMap(([action, authenticationState]: any) => {
                return this.userManagerService.changePassword(
                    action.payload.currentPassword,
                    action.payload.newPassword,
                    authenticationState.currentUser.token,
                    action.payload.token,
                    action.payload.captchaToken,
                    action.payload.captchaSiteKey,
                )
                    .pipe(
                        map((response: any) => {
                            if (response.ok) {
                                return { type: UsersAction.changePasswordSucess, payload: response };
                            }
                            return { type: UsersAction.changePasswordError, response };
                        }),
                        catchError((error) => of({ type: UsersAction.changePasswordError, payload: error })));
            }),
            share());

    @Effect({ dispatch: false })
    public accountSelectRequired$: Observable<UsersAction> = this.actions$
        .pipe(ofType(UsersAction.accountSelectRequired));

    /**
     * Envia e-mail sobre senha esquecida
     */
    @Effect()
    public sendForgotPasswordEmail$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.sendForgotPasswordEmail),
            switchMap((action: any) => {
                return this.userManagerService.sendForgotEmailPassword(action.payload)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: UsersAction.forgotPasswordEmailSuccefulySent };
                            }

                            return { type: UsersAction.forgotPasswordEmailSendingError, payload: result.messages };
                        }),
                        catchError((error) => {
                            return of({ type: UsersAction.forgotPasswordEmailSendingError, payload: error });
                        }));
            }),
            share());
    /**
    * Valida token enviado por e-mail sobre senha esquecida
    */
    @Effect()
    public validateTokenForgotPasswordEmail$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.validateTokenForgotPasswordEmail),
            switchMap((action: any) => {
                return this.userManagerService.validTokenForgotEmailPassword(action.payload)
                    .pipe(
                        map((result: any) => {
                            return (result.value.isSuccess) ?
                                { type: UsersAction.validateTokenForgotPasswordEmailSuccess, payload: { ...result, ...action.payload } } :
                                { type: UsersAction.validateTokenForgotPasswordEmailError, payload: result }
                        }),
                        catchError((error) => {
                            return of({ type: UsersAction.validateTokenForgotPasswordEmailError, payload: error });
                        }));
            }),
            share());

    @Effect()
    public validateTokenForgotPasswordEmailSuccess$: Observable<any> = this.actions$
        .pipe(
            ofType(UsersAction.validateTokenForgotPasswordEmailSuccess),
            switchMap((action: any) => {
                this.router.navigate(['/recuperar-senha'], { state: { name: action.payload.value.name, token: action.payload.value.token, originPath: action.payload.originPath } });
                return new Subject().asObservable();
            }),
            share());
    /**
     * Autentica um usuário a partir de um token recebido via e-mail
     */
    @Effect()
    public authenticateUserUsingToken$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.authenticateUserUsingToken),
            switchMap((action: any) => {
                return this.userManagerService.authenticateUserUsingComplexToken(action.payload.token, action.payload.email)
                    .pipe(
                        map((result) => {
                            if (result.access_token) {
                                this.userManagerService.saveUserPayload(result.access_token);
                                return { type: UsersAction.loginSuccess, payload: UserManagerService.extractUserData(result.access_token) };
                            }

                            return { type: UsersAction.loginError, payload: result };
                        }),
                        catchError((error) => {
                            return of({ type: UsersAction.loginError, payload: error });
                        }));

            }),
            share());

    /**
     * Autentica um usuário a partir de um token recebido do web app
     */
    @Effect()
    public authenticateUserUsingTokenTimeFromWebApp$: Observable<UsersAction> =
        this.actions$
            .pipe(
                ofType(UsersAction.authenticateUserUsingTokenFromWebApp),
                switchMap((action: any) => {
                    return this.userManagerService.authenticateUserUsingComplexTokenFromWebApp(action.payload).pipe(
                        map((result: any) => {
                            if (result.access_token) {
                                this.userManagerService.saveUserPayload(result.access_token);
                                return { type: UsersAction.loginSuccess, payload: UserManagerService.extractUserData(result.access_token) };
                            }

                            return { type: UsersAction.loginError, payload: result };
                        }),
                        catchError((error) => {
                            return of({ type: UsersAction.loginError, payload: error });
                        }));

                }),
                share());

    /**
     * Autentica um usuário a partir de um token recebido do cadastro
     */
    @Effect()
    public authenticateUserUsingTokenTimeFromRegister$: Observable<UsersAction> =
        this.actions$
            .pipe(
                ofType(UsersAction.authenticateUserUsingTokenFromRegister),
                switchMap((action: any) => {
                    return this.userManagerService.authenticateUserUsingComplexTokenFromRegister(action.payload)
                        .pipe(
                            map((result) => {
                                if (result.access_token) {
                                    this.userManagerService.saveUserPayload(result.access_token);
                                    return { type: UsersAction.loginSuccess, payload: UserManagerService.extractUserData(result.access_token) };
                                }
                                return { type: UsersAction.loginError, payload: result };
                            }),
                            catchError((error) => {
                                return of({ type: UsersAction.loginError, payload: error });
                            }),
                        );
                }),
                share());

    /** Recuperar senha */
    @Effect()
    public recoverPassword$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.recoverPassword),
            switchMap((action: any) => {
                return this.userManagerService.recoverPassword(
                    action.payload.password,
                    action.payload.token,
                )
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: UsersAction.recoverPasswordSuccess };
                            }

                            return { type: UsersAction.recoverPasswordError };
                        }),
                        catchError((error) => {
                            return of({ type: UsersAction.recoverPasswordError, payload: error });
                        }));
            }),
            share());

    /** Enviar e-mail de desbloqueio de conta */
    @Effect()
    public sendUnlockEmailAccount$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.requestUnlockAccountEmail),
            withLatestFrom(this.store.select((state) => state.authentication.loginAttempt)),
            switchMap(([action, loginAttempt]) => {
                return this.userManagerService.requestUnlockAccountEmail(loginAttempt.username).pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: UsersAction.requestUnlockAccountEmailSuccess };
                        }

                        return { type: UsersAction.requestUnlockAccountEmailError };
                    }),
                    catchError((error) => {
                        return of({ type: UsersAction.requestUnlockAccountEmailError, payload: error });
                    }));
            }),
            share());

    /** Desbloquear conta */
    @Effect()
    public unlockAccount$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.unlockAccount),
            switchMap((action: any) => {
                return this.userManagerService.unlockAccount(action.payload.firstFactor, action.payload.secondFactor).pipe(
                    map((result: any) => {
                        if (result.ok) {
                            return { type: UsersAction.unlockAccountSuccess };
                        }

                        if (
                            !result.ok &&
                            result.messages.length &&
                            AuthenticationAttemptResult.SecondFactorRequired.equals(result.messages[0].code)
                        ) {
                            return { type: UsersAction.secondFactorNeddedToUnlockAccount };
                        }

                        return { type: UsersAction.unlockAccountError };
                    }),
                    catchError((error) => {
                        return of({ type: UsersAction.unlockAccountError, payload: error });
                    }));
            }),
            share());

    /** Informação sobre quando a senha foi modificada pela última vez */
    @Effect()
    public getLastModifiedPasswordDate$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.getLastModifiedPasswordDate),
            withLatestFrom(this.store.select((state) => state.authentication)),
            filter(([action, state]) => !state.currentUser || !state.currentUser.hasOwnProperty('lastModifyPassword')),
            debounceTime(300),
            switchMap(([action, state]) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.authentication.routes.getLasModifiedPasswordDate.uri)
                    .pipe(
                        map((result: any) => {
                            if (result.ok) {
                                return { type: UsersAction.getLastModifiedPasswordDateSucess, payload: result.value };
                            }

                            return { type: UsersAction.getLastModifiedPasswordDateError };
                        }),
                        retry(3),
                        catchError((error) => {
                            return of({ type: UsersAction.getLastModifiedPasswordDateError, payload: error });
                        }));
            }),
            share());

    /** Verifica se o usuário possiu app ativo */
    @Effect()
    public getHasDevice$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.getHasDevice),
            withLatestFrom(this.store.select((state) => state.authentication)),
            filter(([action, state]) => !state.currentUser || !state.currentUser.hasOwnProperty('hasDevice')),
            debounceTime(300),
            switchMap(([action, state]) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.authentication.routes.getHasDevice.uri)
                    .pipe(
                        map((result) => {
                            if (result.ok) {
                                return { type: UsersAction.getHasDeviceSuccess, payload: result.value.userHasDevice };
                            }

                            return { type: UsersAction.getHasDeviceError };
                        }),
                        retry(3),
                        catchError((error) => {
                            return of({ type: UsersAction.getHasDeviceError, payload: error });
                        }));
            }),
            share());

    @Effect()
    public getUpdatedCblc$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.requestUpdatedCblc),
            switchMap((action) => {
                return this.backendService.get<DefaultResponse>('userprofile/accountcblc')
                    .pipe(
                        map((result) => {
                            if (result.hasError || !result.hasValue || !result.value) {
                                return { type: UsersAction.requestUpdatedCblcError };
                            }

                            const cblcPayload = result
                                .value
                                .find(({ cblcType }) => cblcType === CblcType.Owner);

                            if (!cblcPayload || !cblcPayload.code) {
                                return { type: UsersAction.requestUpdatedCblcError };
                            }

                            return { type: UsersAction.requestUpdatedCblcSuccess, payload: cblcPayload.code };
                        }),
                    );
            }));

    /** Pega SessionID para usuário deslogado */
    @Effect()
    public getSessionID$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.getSessionID),
            switchMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.authentication.routes.getSessionID.uri).pipe(
                    map((result) => {
                        if (result.ok) {
                            return { type: UsersAction.getSessionIDSuccess, payload: result.value };
                        }

                        return { type: UsersAction.getSessionIDError };
                    }),
                    retry(3),
                    catchError((error) => {
                        return of({ type: UsersAction.getSessionIDError, payload: error });
                    }));
            }),
            share());

    @Effect({ dispatch: false })
    public forceAuthenticationRequest$ =
        this.actions$
            .pipe(ofType(UsersAction.forcedAuthenticationFailed, UsersAction.forcedAuthenticationSuccefully));


    /** Pega Array de Cblc**/
    @Effect()
    public getUserCblc$: Observable<UsersAction> = this.actions$
        .pipe(
            ofType(UsersAction.getUserCblc),
            switchMap((action) => {
                return this.backendService.get<DefaultResponse>(RoutesTable.userprofile.routes.getCblcArray.uri,).pipe(
                    map((result) => {

                        if (result.ok) {
                            return { type: UsersAction.getUserCblcArraySuccess, payload: result.value };
                        }

                        return { type: UsersAction.getUserCblcArrayError };
                    }),
                    retry(3),
                    catchError((error) => {
                        return of({ type: UsersAction.getUserCblcArrayError, payload: error });
                    }));
            }),
            share());


    constructor(
        private actions$: Actions<UsersAction>,
        private store: Store<AppState>,
        private userManagerService: UserManagerService,
        private backendService: BackendService,
        private settingsService: SettingsService,
        private router: Router,
    ) { }

}
